'use client'

import { Component, useEffect, useState, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import {
  useDeferredActivation,
  type DeferredActivationStrategy,
} from '@/hooks/useDeferredActivation'

// Use the client (root) entry — this file is `'use client'` and uses
// dynamic(..., { ssr: false }), so the runtime resolves Spline inside a
// client tree. The `/next` subpath exports an async Server Component meant
// for direct RSC use, and rendering it in a client tree throws
// "<X> is an async Client Component" + an uncached-promise Suspense error.
// The historical webpack-resolution reason for `/next` no longer applies —
// this project is Turbopack-only and resolves the root entry fine.
const Spline = dynamic(() => import('@splinetool/react-spline'), { ssr: false })

type SplineErrorBoundaryProps = {
  fallback?: string
  size?: number
  fallbackSize?: number
  children: ReactNode
}

type SplineErrorBoundaryState = {
  hasError: boolean
}

class SplineErrorBoundary extends Component<SplineErrorBoundaryProps, SplineErrorBoundaryState> {
  constructor(props: SplineErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): SplineErrorBoundaryState {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      const { fallback, fallbackSize, size } = this.props
      if (fallback) {
        const imageSize = fallbackSize || size || 80
        return (
          <Image
            src={fallback}
            alt="3D scene preview"
            width={imageSize}
            height={imageSize}
            className="mx-auto h-auto max-w-full"
          />
        )
      }
      return null
    }

    return this.props.children
  }
}

// Spline (Three.js) is heavy (~2MB of JS, 2.4s of script eval). To keep it
// from competing with hydration and first-interaction work (which is what INP
// measures), gate loading on idle time and viewport proximity. LCP-sensitive
// hero scenes can opt into `after-interaction-idle` so the static PNG remains
// the candidate until an input event has ended LCP observation.
// On viewports under 1024px we never load Spline; the PNG fallback stays.
export default function Spliner({
  scene,
  fallback,
  size,
  className,
  fallbackClassName,
  fallbackSize,
  loadStrategy = 'after-visible-idle',
  showFallbackOnDesktop = false,
  fallbackPriority = false,
  minDelayAfterLoad,
}: {
  scene: string
  fallback?: string
  size?: number
  className?: string
  fallbackClassName?: string
  fallbackSize?: number
  loadStrategy?: DeferredActivationStrategy
  showFallbackOnDesktop?: boolean
  fallbackPriority?: boolean
  minDelayAfterLoad?: number
}) {
  const [splineReady, setSplineReady] = useState(false)
  const { ref, active: showSpline } = useDeferredActivation({
    strategy: loadStrategy,
    mediaQuery: '(min-width: 1024px)',
    minDelayAfterLoad,
  })

  useEffect(() => {
    setSplineReady(false)
  }, [scene, showSpline])

  const showFallback = Boolean(fallback && (!showSpline || !splineReady))
  const wrapperClassName =
    showSpline && (splineReady || !showFallbackOnDesktop)
      ? className
      : showFallbackOnDesktop
        ? fallbackClassName ?? className
        : undefined

  return (
    <div
      ref={ref}
      className={cn('spline meticulous-ignore relative', wrapperClassName)}
      style={{ height: size || 50 }}
      data-review="ignore"
    >
      {showSpline && (
        <SplineErrorBoundary fallback={fallback} size={size} fallbackSize={fallbackSize}>
          <Spline
            scene={scene}
            onLoad={() => setSplineReady(true)}
            className={cn(
              'transition-opacity duration-300',
              splineReady ? 'opacity-100' : 'opacity-0',
            )}
          />
        </SplineErrorBoundary>
      )}

      {showFallback && fallback && (
        // Most below-fold scenes keep desktop empty until Spline loads, avoiding
        // a placeholder-then-real-logo swap. Hero scenes can keep the PNG
        // visible on desktop so LCP has a cheap static candidate.
        <Image
          src={fallback}
          alt="3D scene preview"
          width={fallbackSize || size || 80}
          height={fallbackSize || size || 80}
          className={cn(
            'mx-auto h-auto max-w-full transition-opacity duration-300',
            !showFallbackOnDesktop && 'lg:hidden',
            showSpline && 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-100',
          )}
          priority={fallbackPriority}
        />
      )}
    </div>
  )
}
